import Combobox from '@/libs/components/src/lib/form/Combobox';
import { TIMEZONES } from '@/libs/core/src/lib/functions/time';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { InputMasked } from '@chiroup/components/form/InputMasked';
import { Phone } from '@chiroup/components/form/Phone';
import { Select } from '@chiroup/components/form/Select';
import { Toggle } from '@chiroup/components/form/Toggle';
import { ZipCodeInput } from '@chiroup/components/form/ZipCodeInput';
import { GLOBAL_PLACE_OF_SERVICE_OPTIONS } from '@chiroup/core/constants/globals';
import { CANADIAN_PROVINCES, STATES } from '@chiroup/core/constants/UsStatesAndTerritories';
import { ClinicLocation } from '@chiroup/core/types/Clinic.type';
import { FormFieldErrors } from '@chiroup/core/types/ErrorResponse.type';
import { ValueOf } from '@chiroup/core/types/ValueOf.type';
import React from 'react';
import { COUNTRIES } from '../../../../constants';
import Uploader from '../../../common/uploader/Uploader';
import SectionContainer from '../../../layout/SectionContainer';
import SectionHeader from '../../../layout/SectionHeader';

type Props = {
  value: Partial<ClinicLocation>;
  fieldErrors?: FormFieldErrors;
  onChange: (key: keyof Partial<ClinicLocation>) => (val: ValueOf<ClinicLocation>) => void;
  /** When true, every input is locked. Used when the admin lacks edit rights. */
  disabled?: boolean;
};

const ClinicLocationDetailForm: React.FC<Props> = ({
  value,
  fieldErrors,
  onChange,
  disabled = false,
}) => {
  return (
    <>
      <Input
        name="name"
        className="col-span-4"
        label={value.primary ? 'Name *' : 'Name'}
        value={value.name}
        onChange={onChange('name')}
        errors={fieldErrors?.name}
        hint={value.primary ? '' : 'Inherited from primary location if not provided'}
        disabled={disabled}
      />
      <Input
        name="address"
        className="col-span-2"
        label="Address"
        value={value.address1}
        onChange={onChange('address1')}
        errors={fieldErrors?.address1}
        disabled={disabled}
      />
      <Input
        name="address2"
        className="col-span-2"
        label="Address cont."
        value={value.address2}
        onChange={onChange('address2')}
        errors={fieldErrors?.address2}
        disabled={disabled}
      />
      <Input
        name="city"
        className="col-span-2"
        label="City"
        value={value.city}
        onChange={onChange('city')}
        errors={fieldErrors?.city}
        disabled={disabled}
      />
      {['USA', 'CAN'].includes(value?.country || '') ? (
        <Combobox
          name="state"
          className="col-span-3"
          label={`${value?.country === 'CAN' ? 'Province *' : 'State *'}`}
          value={value.state}
          onChange={onChange('state')}
          errors={fieldErrors?.state}
          options={value?.country === 'CAN' ? CANADIAN_PROVINCES : STATES}
          limit={1}
          disabled={disabled}
        />
      ) : (
        <Input
          name="state"
          className="col-span-3"
          label="State/Province"
          value={value.state}
          onChange={onChange('state')}
          errors={fieldErrors?.state}
          disabled={disabled}
        />
      )}
      <ZipCodeInput
        name="zip"
        className="col-span-2"
        label="Zip/Postal Code"
        value={value.zip}
        onChange={onChange('zip')}
        errors={fieldErrors?.zip}
        country={value.country ?? ''}
        disabled={disabled}
      />
      <Select
        name="country"
        className="col-span-2"
        label="Country *"
        options={COUNTRIES}
        value={value.country ?? ''}
        onChange={onChange('country')}
        errors={fieldErrors?.country}
        disabled={disabled}
      />
      <Checkbox
        name="addressSameAsLocation"
        value={value.billingAddressSameAsLocation}
        onChange={onChange('billingAddressSameAsLocation')}
        label="Use my location address for billing"
        disabled={disabled || value.billingAddressSameAsPrimary}
        className={value.primary ? 'col-span-4' : 'col-span-2'}
      />
      {!value.primary && (
        <Checkbox
          name="addressSameAsPrimary"
          value={value.billingAddressSameAsPrimary}
          onChange={onChange('billingAddressSameAsPrimary')}
          label="Use the primary address for billing"
          disabled={disabled || value.billingAddressSameAsLocation}
          className="col-span-2"
        />
      )}
      {!value.billingAddressSameAsLocation && !value.billingAddressSameAsPrimary && (
        <div className="col-span-4 border rounded-md">
          <SectionContainer>
            <SectionHeader
              title={<p className="text-base font-light text-gray-600">Billing Address</p>}
            />
            <div className="p-4 space-y-1 sm:space-y-0 sm:grid sm:grid-cols-4 sm:gap-4">
              <Input
                name="billingAddress"
                className="col-span-2"
                label="Address"
                value={value.billingAddress1}
                onChange={onChange('billingAddress1')}
                errors={fieldErrors?.billingAddress1}
                disabled={disabled}
              />
              <Input
                name="billingAddress2"
                className="col-span-2"
                label="Address cont."
                value={value.billingAddress2}
                onChange={onChange('billingAddress2')}
                errors={fieldErrors?.billingAddress2}
                disabled={disabled}
              />
              <Input
                name="billingCity"
                className="col-span-2"
                label="City"
                value={value.billingCity}
                onChange={onChange('billingCity')}
                errors={fieldErrors?.billingCity}
                disabled={disabled}
              />
              <Combobox
                name="billingState"
                className="col-span-2"
                label={`${value?.billingCountry === 'CAN' ? 'Province *' : 'State *'}`}
                value={value.billingState}
                onChange={onChange('billingState')}
                errors={fieldErrors?.billingState}
                options={value?.country === 'CAN' ? CANADIAN_PROVINCES : STATES}
                limit={1}
                disabled={disabled}
              />
              <ZipCodeInput
                name="billingZip"
                className="col-span-2"
                label="Zip/Postal Code"
                value={value.billingZip}
                onChange={onChange('billingZip')}
                errors={fieldErrors?.billingZip}
                country={value.billingCountry ?? ''}
                disabled={disabled}
              />
              <Combobox
                name="billingCountry"
                className="col-span-2"
                label="Country"
                options={COUNTRIES}
                value={value.billingCountry ?? ''}
                onChange={onChange('billingCountry')}
                errors={fieldErrors?.billingCountry}
                limit={1}
                disabled={disabled}
              />
            </div>
          </SectionContainer>
        </div>
      )}
      <Combobox
        name="timezone"
        className="col-span-2"
        label="Time zone *"
        options={TIMEZONES}
        value={value.timezone}
        onChange={onChange('timezone')}
        errors={fieldErrors?.timezone}
        limit={1}
        disabled={disabled}
      />
      <Input
        name="npiNumber"
        className="col-span-2"
        label="NPI number"
        value={value.npiNumber}
        onChange={onChange('npiNumber')}
        errors={fieldErrors?.npiNumber}
        disabled={disabled}
      />
      <InputMasked
        name="serviceTaxRate"
        label="Service tax rate"
        placeholder="#.###"
        numericOptions={{
          allowNegative: false,
          decimalScale: 3,
        }}
        value={value.serviceTaxRate}
        onChange={onChange('serviceTaxRate')}
        errors={fieldErrors?.serviceTaxRate}
        className="col-span-2"
        disabled={disabled}
      />
      <InputMasked
        name="productTaxRate"
        label="Product tax rate"
        placeholder="#.###"
        numericOptions={{
          allowNegative: false,
          decimalScale: 3,
        }}
        value={value.productTaxRate}
        onChange={onChange('productTaxRate')}
        errors={fieldErrors?.productTaxRate}
        className="col-span-2"
        disabled={disabled}
      />
      <Combobox
        name="placeOfService"
        className="col-span-4"
        label="Place of service"
        options={GLOBAL_PLACE_OF_SERVICE_OPTIONS}
        value={value.placeOfService}
        onChange={onChange('placeOfService')}
        errors={fieldErrors?.placeOfService}
        limit={1}
        disabled={disabled}
      />
      <Input
        name="path"
        className="col-span-2"
        label="Vanity URL suffix"
        value={value.path}
        onChange={onChange('path')}
        errors={fieldErrors?.path}
        hint="This will be used to create a vanity URL for your clinic."
        disabled={disabled}
      />
      <Phone
        name="phone"
        className="col-span-2"
        label="Phone"
        value={value.phone}
        onChange={onChange('phone')}
        errors={fieldErrors?.phone}
        disabled={disabled}
      />
      <Phone
        name="fax"
        className="col-span-2"
        label="Fax"
        value={value.fax}
        onChange={onChange('fax')}
        errors={fieldErrors?.fax}
        disabled={disabled}
      />
      <Input
        name="email"
        className="col-span-2"
        label="Email"
        value={value.email}
        onChange={onChange('email')}
        errors={fieldErrors?.email}
        disabled={disabled}
      />
      <Input
        name="site"
        className="col-span-2"
        label="Site"
        value={value.site}
        onChange={onChange('site')}
        errors={fieldErrors?.site}
        disabled={disabled}
      />
      <Input
        name="facebook"
        className="col-span-2"
        label="Facebook"
        value={value.facebook}
        onChange={onChange('facebook')}
        errors={fieldErrors?.facebook}
        disabled={disabled}
      />
      <Input
        name="x"
        className="col-span-2"
        label="X"
        value={value.x}
        onChange={onChange('x')}
        errors={fieldErrors?.x}
        disabled={disabled}
      />
      <Input
        name="youtube"
        className="col-span-2"
        label="Youtube"
        value={value.youtube}
        onChange={onChange('youtube')}
        errors={fieldErrors?.youtube}
        disabled={disabled}
      />
      <Input
        name="reviewURL"
        className="col-span-4"
        label="Review URL"
        value={value.reviewURL}
        onChange={onChange('reviewURL')}
        errors={fieldErrors?.reviewURL}
        disabled={disabled}
      />
      <Input
        name="welcomePage"
        className="col-span-4"
        label="Welcome page"
        value={value.welcomePage}
        onChange={onChange('welcomePage')}
        errors={fieldErrors?.welcomePage}
        tooltip="This URL is sent to patients when they schedule their first appointment, providing them with information about their upcoming visit."
        disabled={disabled}
      />
      <Uploader
        name="img"
        label="Logo"
        onChange={onChange('logo')}
        className="col-span-3"
        value={value.logo}
        type="image"
        uploadCategory="logos"
        uploadType="image"
        fileTypes={['png', 'jpg', 'jpeg', 'gif']}
        fileSizeLimit={10}
        resize
        disabled={disabled}
      />
      <Toggle
        label="Primary location"
        className="col-span-4"
        onChange={onChange('primary')}
        value={!!value.primary}
        disabled={disabled}
      />
      <Toggle
        label="Active"
        className="col-span-4"
        onChange={onChange('active')}
        value={!!value.active}
        disabled={disabled}
      />
    </>
  );
};

export default ClinicLocationDetailForm;
